/**
 * THE SERVICE WORKER, FROM THE PAGE'S SIDE — AND THE PUSH SUBSCRIPTION WITH IT.
 *
 * WHY THIS FILE IS SO CAREFUL ABOUT WHAT IT DOES NOT DO. A service worker is the
 * one piece of this product that keeps running after the page is closed, and
 * the Owner's brief draws a hard line around what may live on the device:
 * conversations, findings, sources and receipts are read live and must never be
 * cached. The worker itself is therefore written to cache the SHELL and nothing
 * else — and this file is written to give it nothing else to cache. There is no
 * "cache this response" call here, no `fetch` interceptor registered from this
 * side, and no credential of any kind passed across the boundary.
 *
 * THE REGISTRATION IS ALLOWED TO FAIL, AND ITS FAILURE IS NOT AN ERROR. A
 * browser without HTTPS, a browser with site data disabled, a browser that has
 * never heard of service workers — all of them get a workspace that works
 * exactly as it did before this file existed, because the worker only ever adds
 * an offline shell and a push receiver. So nothing here throws, nothing here
 * blocks the render, and the one thing that would be dishonest — reporting a
 * registration that did not happen — is what `registration` on the returned
 * state is for.
 *
 * UPDATES ARE THE OWNER'S TO TAKE. A worker that calls `skipWaiting()` on
 * install swaps the running page's world out from under it mid-session, which
 * for this product means a view could change its mind about a payload shape
 * between two reads. So the new worker is left WAITING, a signal is raised, and
 * the shell offers a reload. That is the whole of the update policy, and it is
 * three lines of behaviour rather than a library.
 */

import { signal } from '@preact/signals';

/** Where the worker is served from. It is a real static file beside the shell —
 *  not a hashed asset — because the registration URL is also the worker's
 *  SCOPE, and a scope that changed every build would orphan every client. */
export const SERVICE_WORKER_PATH = '/sw.js';

/** What the page knows about its worker, in one object so a view can render it
 *  without asking four questions. */
export interface ServiceWorkerState {
	/** `unsupported` is a browser fact; `refused` is a registration that was
	 *  attempted and did not take; `registered` means the browser accepted it.
	 *  `null` is "not asked yet", which is different from all three. */
	status: 'unknown' | 'unsupported' | 'refused' | 'registered';
	/** True when a newer worker has installed and is waiting for the page to go
	 *  away. The shell offers a reload; nothing reloads on its own. */
	updateWaiting: boolean;
	detail: string | null;
}

export const serviceWorkerState = signal<ServiceWorkerState>({
	status: 'unknown',
	updateWaiting: false,
	detail: null,
});

function describe(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

/**
 * REGISTER, AND WATCH FOR A REPLACEMENT.
 *
 * `updateViaCache: 'none'` IS LOAD-BEARING AND NOT A DEFAULT. Without it the
 * browser may answer a byte-range request for `/sw.js` out of the HTTP cache,
 * and a worker that cannot be re-fetched is a worker that cannot be replaced —
 * the classic way a bad service worker becomes permanent. The file is one small
 * script; revalidating it on a page load costs nothing.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerState> {
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
		const state: ServiceWorkerState = {
			status: 'unsupported',
			updateWaiting: false,
			detail: 'this browser has no service worker, so the workspace runs without an offline shell',
		};
		serviceWorkerState.value = state;
		return state;
	}
	try {
		// A RELATIVE URL WOULD RESOLVE AGAINST THE CURRENT PATH, and this
		// workspace is served at every path — `/evidence/adj:1` is as real as
		// `/chat`. A relative registration there would ask for
		// `/evidence/sw.js`, which does not exist. The leading slash is the
		// difference between one worker and a 404 per view.
		const registration = await navigator.serviceWorker.register(SERVICE_WORKER_PATH, {
			scope: '/',
			updateViaCache: 'none',
		});

		const noteUpdate = (): void => {
			// `installed` WITH A CONTROLLER MEANS "REPLACEMENT", NOT "FIRST
			// INSTALL". On a first visit the new worker also reaches `installed`
			// and also has `waiting` set, and telling a first-time visitor to
			// reload for an update they never had would be nonsense.
			if (registration.waiting === null || navigator.serviceWorker.controller === null) return;
			serviceWorkerState.value = {
				status: 'registered',
				updateWaiting: true,
				detail: 'a newer version of the workspace shell is ready',
			};
		};
		noteUpdate();
		registration.addEventListener('updatefound', () => {
			const installing = registration.installing;
			if (installing === null) return;
			installing.addEventListener('statechange', noteUpdate);
		});

		const state: ServiceWorkerState = {
			status: 'registered',
			updateWaiting: serviceWorkerState.value.updateWaiting,
			detail: null,
		};
		serviceWorkerState.value = state;
		return state;
	} catch (error) {
		const state: ServiceWorkerState = {
			status: 'refused',
			updateWaiting: false,
			detail: describe(error),
		};
		serviceWorkerState.value = state;
		return state;
	}
}

/** Ask the browser to check for a new worker. Called when the Owner takes the
 *  update, so the reload is not racing a check that has not run. */
export async function checkForShellUpdate(): Promise<void> {
	if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
	const registration = await navigator.serviceWorker.getRegistration('/');
	await registration?.update();
}

/**
 * PUSH — CAPABILITY, PERMISSION AND SUBSCRIPTION, EACH ASKED SEPARATELY.
 *
 * The three questions have three different answers and only one of them is
 * about Scout. A browser may have no push at all; a browser may have push and a
 * person may have refused it; a browser may have both and this device may have
 * no subscription. The Notifications screen needs all three to say anything
 * true, so they are reported rather than flattened into `supported: false`.
 */
export type PushCapability =
	| { kind: 'unsupported'; detail: string }
	| { kind: 'permission'; permission: NotificationPermission }
	| { kind: 'ready'; permission: 'granted' };

export function pushCapability(): PushCapability {
	if (typeof navigator === 'undefined' || typeof window === 'undefined') {
		return { kind: 'unsupported', detail: 'there is no browser here' };
	}
	if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
		return {
			kind: 'unsupported',
			detail: 'this browser has no Push API, so Scout cannot send this device a notification',
		};
	}
	if (!('Notification' in window)) {
		return { kind: 'unsupported', detail: 'this browser has no Notification API' };
	}
	const permission = Notification.permission;
	if (permission !== 'granted') return { kind: 'permission', permission };
	return { kind: 'ready', permission: 'granted' };
}

/**
 * A VAPID PUBLIC KEY ARRIVES AS BASE64URL AND MUST BECOME BYTES.
 *
 * `applicationServerKey` takes a `BufferSource`, and passing it the string — the
 * obvious mistake — throws `InvalidCharacterError` on the `-` and `_`. The
 * padding is restored before decoding because base64url omits it and `atob`
 * requires it.
 */
export function decodeApplicationServerKey(base64Url: string): Uint8Array<ArrayBuffer> {
	const padded = base64Url.replace(/-/g, '+').replace(/_/g, '/');
	const withPadding = padded + '='.repeat((4 - (padded.length % 4)) % 4);
	const binary = atob(withPadding);
	// THE BUFFER IS ALLOCATED EXPLICITLY AND THE TYPE SAYS SO. `new
	// Uint8Array(length)` is typed as `Uint8Array<ArrayBufferLike>`, which is
	// not assignable to `BufferSource` — the WebCrypto and Push API types want
	// an `ArrayBuffer` and a `SharedArrayBuffer` is a different thing. Naming
	// the buffer parameter is what makes this a `Uint8Array<ArrayBuffer>` and
	// lets it be passed to `pushManager.subscribe` without a cast.
	const bytes = new Uint8Array(new ArrayBuffer(binary.length));
	for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
	return bytes;
}

export interface BrowserSubscription {
	endpoint: string;
	keys: { p256dh: string; auth: string };
}

/** The body the subscribe route takes — the browser's own shape, narrowed to
 *  the two key members Scout stores and nothing else. A `PushSubscriptionJSON`
 *  also carries an `expirationTime` the server has no use for, and forwarding
 *  a whole browser object into a request body is how a field nobody validated
 *  ends up in a table. */
export function subscriptionBody(subscription: PushSubscription): BrowserSubscription | null {
	const json = subscription.toJSON() as { endpoint?: unknown; keys?: { p256dh?: unknown; auth?: unknown } };
	if (typeof json.endpoint !== 'string' || json.endpoint.length === 0) return null;
	const p256dh = json.keys?.p256dh;
	const auth = json.keys?.auth;
	if (typeof p256dh !== 'string' || typeof auth !== 'string') return null;
	return { endpoint: json.endpoint, keys: { p256dh, auth } };
}

/**
 * ASK FOR A SUBSCRIPTION, CREATING ONE IF THE DEVICE HAS NONE.
 *
 * `userVisibleOnly: true` IS NOT OPTIONAL AND IS NOT A HINT. It is the promise
 * that every push this Worker sends results in something the Owner can see.
 * A subscription without it is refused by Chrome outright, and one that lied
 * about it would be a silent background channel into somebody's phone.
 *
 * THE PERMISSION PROMPT IS ASKED HERE AND NOWHERE ELSE, and only from a press —
 * see `views/notifications.tsx`. A prompt raised on page load is a prompt
 * refused on principle, and once refused it cannot be asked again.
 */
export async function subscribeToPush(
	applicationServerKey: string,
): Promise<{ ok: true; subscription: BrowserSubscription } | { ok: false; detail: string }> {
	const capability = pushCapability();
	if (capability.kind === 'unsupported') return { ok: false, detail: capability.detail };
	let permission: NotificationPermission = capability.permission === 'granted' ? 'granted' : capability.permission;
	if (permission !== 'granted') permission = await Notification.requestPermission();
	if (permission !== 'granted') {
		return {
			ok: false,
			detail:
				permission === 'denied'
					? 'this browser has been told to refuse notifications for Scout, and a page cannot ask again — the setting is the browser’s, not this workspace’s'
					: 'the notification permission was not granted',
		};
	}
	const registration = await navigator.serviceWorker.getRegistration('/');
	if (registration === undefined) {
		return { ok: false, detail: 'no service worker is registered, so there is nothing to receive a push' };
	}
	// AWAIT THE WORKER BEING READY. On a first visit the registration resolves
	// before the worker is active, and `pushManager.subscribe` on an inactive
	// registration rejects with `InvalidStateError` — which reads as a broken
	// browser rather than as "try again in a moment".
	await navigator.serviceWorker.ready;
	const existing = await registration.pushManager.getSubscription();
	const subscription =
		existing ??
		(await registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: decodeApplicationServerKey(applicationServerKey),
		}));
	const body = subscriptionBody(subscription);
	if (body === null) {
		return { ok: false, detail: 'the browser returned a subscription without the keys Scout has to store' };
	}
	return { ok: true, subscription: body };
}

/** Whether THIS DEVICE already holds a subscription — the browser's answer, not
 *  the server's. Both are needed: a device can hold one the server has revoked,
 *  and the server can hold one the device has dropped. */
export async function currentPushSubscription(): Promise<BrowserSubscription | null> {
	if (pushCapability().kind === 'unsupported') return null;
	const registration = await navigator.serviceWorker.getRegistration('/');
	if (registration === undefined) return null;
	const subscription = await registration.pushManager.getSubscription();
	if (subscription === null) return null;
	return subscriptionBody(subscription);
}

/**
 * DROP THE SUBSCRIPTION AT THE BROWSER, AND SAY WHETHER THAT WORKED.
 *
 * The server row is revoked separately and first, because a browser-side
 * `unsubscribe()` that succeeds while the server still holds the endpoint
 * leaves the endpoint live until the push service answers 410 — and until then
 * Scout would be sending notifications to a device that asked to stop.
 */
export async function dropPushSubscription(): Promise<boolean> {
	if (pushCapability().kind === 'unsupported') return false;
	const registration = await navigator.serviceWorker.getRegistration('/');
	if (registration === undefined) return false;
	const subscription = await registration.pushManager.getSubscription();
	if (subscription === null) return true;
	return await subscription.unsubscribe();
}

/** The permission, without prompting for it. */
export function notificationPermission(): NotificationPermission | 'unsupported' {
	if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
	return Notification.permission;
}

/**
 * A SHORT, STABLE, NON-REVERSIBLE NAME FOR AN ENDPOINT — THE CLIENT'S HALF.
 *
 * WHY THIS EXISTS AT ALL. The push endpoint plus its two keys is a CAPABILITY:
 * anybody holding it can send that device a notification. So `GET
 * /gateway/push` deliberately does not return it, and the server sends this
 * digest instead. The consequence is that the browser, which knows its own
 * endpoint and cannot ask the server for it, has to be able to compute the same
 * value — which is why the derivation is written down here as well as there,
 * and why a specimen pins the two against each other.
 *
 * IT IS NOT A SECURITY BOUNDARY AND IT MUST NOT BE TREATED AS ONE. The only
 * thing it decides is which row a screen offers to revoke, inside a payload
 * that already required a verified Owner session to read. What it must not do
 * is make the endpoint RECOVERABLE, which a one-way digest does not.
 *
 * THE ALGORITHM IS THE REPOSITORY'S OWN AND NOT A NEW ONE. It is FNV-1a over
 * the UTF-16 code units of the string, 32 bits, lowercase hex, zero-padded to
 * eight — exactly `stableDigest` in `worker/scout/core/observations.ts`, which
 * is what mints Scout's notification ids. A second hashing convention invented
 * for this one field would be a second thing to keep true.
 *
 * THE INPUT IS THE RAW ENDPOINT STRING, EXACTLY AS THE BROWSER GIVES IT. No
 * trimming, no lowercasing, no URL normalisation: a push endpoint is an opaque
 * capability URL, and two strings that differ by a character are two different
 * devices. Normalising here and not there is exactly how the two halves would
 * stop agreeing.
 */
export function endpointFingerprintOf(endpoint: string): string {
	let hash = 0x811c9dc5;
	for (let index = 0; index < endpoint.length; index += 1) {
		hash ^= endpoint.charCodeAt(index);
		hash = Math.imul(hash, 0x01000193) >>> 0;
	}
	return hash.toString(16).padStart(8, '0');
}
